import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { t } from "@/i18n";

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success(t('toasts.resetEmailSent'));
    } catch (err) {
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-md mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-[#111827] mb-2">{t('forgotPasswordPage.checkEmail')}</h1>
          <p className="text-[#6b7280] mb-6">
            {t('forgotPasswordPage.emailSent', { email })}
          </p>
          <Link to="/auth">
            <Button className="btn-primary rounded-xl">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('forgotPasswordPage.backToLogin')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 md:px-12 lg:px-24 py-12">
      <div className="max-w-md mx-auto">
        <Link 
          to="/auth" 
          className="inline-flex items-center gap-2 text-[#6b7280] hover:text-[#111827] mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('forgotPasswordPage.backToLogin')}
        </Link>

        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#1a4d2e]/10 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-[#1a4d2e]" />
          </div>
          <h1 className="text-3xl font-bold text-[#111827] mb-2">{t('forgotPasswordPage.title')}</h1>
          <p className="text-[#6b7280]">
            {t('forgotPasswordPage.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-[#111827]">{t('forgotPasswordPage.emailLabel')}</Label>
            <Input
              id="email"
              data-testid="forgot-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.emailPlaceholder')}
              required
              className="mt-1"
            />
          </div>

          <Button
            data-testid="send-reset-btn"
            type="submit"
            disabled={loading}
            className="w-full btn-primary rounded-xl py-3 font-semibold"
          >
            {loading ? t('forgotPasswordPage.sending') : t('forgotPasswordPage.sendLink')}
          </Button>
        </form>
      </div>
    </div>
  );
};
